import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CHAT_TRANSPORT } from './chat-transport';
import { ChatEvent, ChatInput, ChatMessage, TextBlock, ToolBlock, ToolCall } from './chat.types';

/**
 * Provider-agnostic store that folds a transport's `ChatEvent` stream into a
 * renderable `ChatMessage[]` (each an ordered list of typed blocks) plus streaming/error
 * state. Provided per chat surface (no `providedIn`), so each `<ai-chat>` host gets an
 * isolated instance.
 */
@Injectable()
export class ChatStateStore {
  private readonly transport = inject(CHAT_TRANSPORT);

  private readonly _messages = signal<ChatMessage[]>([]);
  private readonly _isStreaming = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly messages = this._messages.asReadonly();
  readonly isStreaming = this._isStreaming.asReadonly();
  readonly error = this._error.asReadonly();

  constructor() {
    this.transport.events$.pipe(takeUntilDestroyed()).subscribe({
      next: (event) => this.handleEvent(event),
      error: (err) => this.setError(err instanceof Error ? err.message : 'Stream failed'),
      complete: () => this.finalizeOpenMessage(),
    });
  }

  send(input: ChatInput): void {
    if (input.kind === 'text') {
      const text = input.text.trim();
      if (text) {
        this._messages.update((msgs) => [...msgs, userMessage(text)]);
      }
    }
    this.transport.send(input);
  }

  /** Clear the conversation (messages, streaming, and error state). */
  reset(): void {
    this._messages.set([]);
    this._isStreaming.set(false);
    this._error.set(null);
  }

  private handleEvent(event: ChatEvent): void {
    switch (event.kind) {
      case 'text':
        this.appendText(event.delta, event.messageId);
        break;
      case 'tool-start':
        this.startTool(event.id, event.name, event.description, event.args);
        break;
      case 'tool-progress':
        this.progressTool(event.id, event.status, event.progressMessage);
        break;
      case 'tool-complete':
        this.completeTool(event.id, event.result, event.latency);
        break;
      case 'final':
        this.finalizeOpenMessage();
        break;
      case 'error':
        this.setError(event.message);
        break;
      case 'plan':
      case 'questions':
      case 'proposed-action':
      case 'input-validation':
        break;
    }
  }

  private appendText(delta: string, messageId?: string): void {
    this._isStreaming.set(true);
    this._error.set(null);
    this._messages.update((msgs) => {
      const working = [...msgs];
      let idx = openAssistantIndex(working);
      if (idx === -1) {
        working.push(assistantMessage(messageId ?? crypto.randomUUID()));
        idx = working.length - 1;
      }
      const msg = working[idx];
      const lastIdx = msg.blocks.length - 1;
      const last = msg.blocks[lastIdx];
      const blocks =
        last?.kind === 'text'
          ? msg.blocks.map((b, i) => (i === lastIdx && b.kind === 'text' ? { ...b, text: b.text + delta } : b))
          : [...msg.blocks, textBlock(delta)];
      working[idx] = { ...msg, blocks };
      return working;
    });
  }

  private startTool(id: string, name: string, description?: string, args?: string): void {
    this._isStreaming.set(true);
    this._messages.update((msgs) => {
      const working = [...msgs];
      let idx = openAssistantIndex(working);
      if (idx === -1) {
        working.push(assistantMessage(crypto.randomUUID()));
        idx = working.length - 1;
      }
      const msg = working[idx];
      if (msg.blocks.some((b) => b.kind === 'tool' && b.tool.id === id)) {
        return msgs;
      }
      const tool: ToolCall = { id, name, state: 'running' };
      if (description !== undefined) {
        tool.description = description;
      }
      if (args !== undefined) {
        tool.args = args;
      }
      working[idx] = { ...msg, blocks: [...msg.blocks, toolBlock(tool)] };
      return working;
    });
  }

  private progressTool(id: string, status?: string, progressMessage?: string): void {
    this._isStreaming.set(true);
    this.updateTool(id, (t) => {
      const next = { ...t };
      if (status !== undefined) {
        next.status = status;
      }
      if (progressMessage !== undefined) {
        next.progressMessage = progressMessage;
      }
      return next;
    });
  }

  private completeTool(id: string, result?: string, latency?: string): void {
    this.updateTool(id, (t) => {
      const next: ToolCall = { ...t, state: 'complete' };
      if (result !== undefined) {
        next.result = result;
      }
      if (latency !== undefined) {
        next.latency = latency;
      }
      return next;
    });
  }

  private updateTool(id: string, fn: (tool: ToolCall) => ToolCall): void {
    this._messages.update((msgs) =>
      msgs.map((m) =>
        m.blocks.some((b) => b.kind === 'tool' && b.tool.id === id)
          ? { ...m, blocks: m.blocks.map((b) => (b.kind === 'tool' && b.tool.id === id ? { ...b, tool: fn(b.tool) } : b)) }
          : m,
      ),
    );
  }

  private finalizeOpenMessage(): void {
    this._isStreaming.set(false);
    this._messages.update((msgs) => {
      const idx = openAssistantIndex(msgs);
      if (idx === -1) {
        return msgs;
      }
      const updated = [...msgs];
      updated[idx] = { ...updated[idx], status: 'complete' };
      return updated;
    });
  }

  private setError(message: string): void {
    this._error.set(message);
    this._isStreaming.set(false);
    this._messages.update((msgs) => {
      const idx = openAssistantIndex(msgs);
      if (idx === -1) {
        return msgs;
      }
      const updated = [...msgs];
      updated[idx] = { ...updated[idx], status: 'error', errorMessage: message };
      return updated;
    });
  }
}

function openAssistantIndex(msgs: ChatMessage[]): number {
  const last = msgs.length - 1;
  if (last >= 0 && msgs[last].role === 'assistant' && msgs[last].status === 'streaming') {
    return last;
  }
  return -1;
}

function textBlock(text: string): TextBlock {
  return { kind: 'text', id: crypto.randomUUID(), text };
}

function toolBlock(tool: ToolCall): ToolBlock {
  return { kind: 'tool', id: tool.id, tool };
}

function userMessage(text: string): ChatMessage {
  return { id: crypto.randomUUID(), role: 'user', status: 'complete', blocks: [textBlock(text)] };
}

function assistantMessage(id: string): ChatMessage {
  return { id, role: 'assistant', status: 'streaming', blocks: [] };
}
